import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProcessingType } from '@/types';
import { Header } from '@/components/layout/Header';
import {
  Shield, Database, FileText, BarChart3, TrendingUp,
  Users, DollarSign, Lock
} from 'lucide-react';
import { logger } from '@/utils/logger';
import { calculateEstimatedCost } from '@/utils';
import {
  BatchUploadSection,
  BatchPricingTable,
  BatchProcessingTypeCard,
  BatchContinueButton,
} from '@/components/batch/landing';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [processableRows, setProcessableRows] = useState(0);
  const [selectedProcessingType, setSelectedProcessingType] = useState<string>('enrich_validate');
  const [analysisJobId, setAnalysisJobId] = useState<string | undefined>();
  const [analysisBucket, setAnalysisBucket] = useState<string | undefined>();
  const [analysisKey, setAnalysisKey] = useState<string | undefined>();
  const [analysisTotalRows, setAnalysisTotalRows] = useState<number>(0);
  const [aiOverviewEnabled, setAiOverviewEnabled] = useState(false);

  const isAiOverviewEligible = (type: string) => type === 'scoring' || type === 'scoring_enrich';

  const handleFileUploaded = (data: {
    file: File;
    csvPreview: Record<string, string>[];
    totalRows: number;
    processableRows: number;
    flaggedRows: number;
    analysisJobId: string;
    analysisBucket: string;
    analysisKey: string;
    analysisTotalRows: number;
  }) => {
    setUploadedFile(data.file);
    setTotalRows(data.totalRows);
    setProcessableRows(data.processableRows);
    setAnalysisJobId(data.analysisJobId);
    setAnalysisBucket(data.analysisBucket);
    setAnalysisKey(data.analysisKey);
    setAnalysisTotalRows(data.analysisTotalRows);
  };

  const handleFileUploadError = (error: Error) => {
    logger.error('File upload error in landing page', { error: error.message });
    // You could show a toast notification here
  };

  const handleProcessingTypeSelect = (type: string) => {
    const eligibleForAi = isAiOverviewEligible(type);

    if (!eligibleForAi && aiOverviewEnabled) {
      setAiOverviewEnabled(false);
    }

    setSelectedProcessingType(type);
  };

  const handleAiOverviewToggle = (enabled: boolean) => {
    if (!isAiOverviewEligible(selectedProcessingType)) {
      return;
    }
    setAiOverviewEnabled(enabled);
  };

  const handleContinueToProcessing = () => {
    if (!uploadedFile) {
      logger.warn('Continue to Processing clicked without uploaded file');
      return;
    }

    logger.info('Continuing to payment page', {
      selectedType: selectedProcessingType,
      fileName: uploadedFile.name,
      processableRows,
      totalRows
    });

    if (!analysisBucket || !analysisKey) {
      logger.warn('Attempted to continue without completed analysis');
      return;
    }

    const selectedType = selectedProcessingType as ProcessingType;
    const includeAiOverview = aiOverviewEnabled && isAiOverviewEligible(selectedType);
    const estimatedCost = calculateEstimatedCost(processableRows, selectedType, {
      aiOverview: includeAiOverview,
    });
    const maxCost = calculateEstimatedCost(totalRows, selectedType, {
      aiOverview: includeAiOverview,
    });

    navigate('/batch/payment', {
      state: {
        jobId: analysisJobId,
        file: uploadedFile,
        processingType: selectedProcessingType,
        processableRows,
        totalRows,
        estimatedCost,
        maxPossibleCost: maxCost,
        s3Key: analysisKey,
        s3Bucket: analysisBucket,
        fileName: uploadedFile.name,
        analysisTotalRows: analysisTotalRows || totalRows,
        aiOverview: includeAiOverview,
      }
    });
  };

  const selectedType = selectedProcessingType as ProcessingType;
  const includeAiOverview = aiOverviewEnabled && isAiOverviewEligible(selectedType);
  const estimatedCost = uploadedFile ? calculateEstimatedCost(processableRows, selectedType, {
    aiOverview: includeAiOverview,
  }) : 0;

  return (
    <div className="min-h-screen">
      <Header />

      {/* Professional Hero Section */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            {/* Professional Headline */}
            <h1 className="h1 mb-3">
              Upload. Verify. Enhance.
            </h1>
            <p className="body max-w-3xl mx-auto text-slate-600 mb-6">
              Advanced skip tracing, contact validation, and AI-powered scoring models. Process identity
              verification, data enhancement, and recovery analytics trusted by agencies and financial institutions.
            </p>

            {/* Industry Use Cases */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <DollarSign className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Debt Collection</div>
                <div className="text-xs text-slate-500">Advanced recovery scoring</div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <Shield className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Identity Verification</div>
                <div className="text-xs text-slate-500">KYC compliance</div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <Lock className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Fraud Prevention</div>
                <div className="text-xs text-slate-500">Synthetic detection</div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <FileText className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Legal Services</div>
                <div className="text-xs text-slate-500">Process serving</div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <Users className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Real Estate</div>
                <div className="text-xs text-slate-500">Owner contact</div>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-2 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                  <TrendingUp className="h-5 w-5 text-slate-600" />
                </div>
                <div className="text-sm font-medium text-slate-900">Marketing</div>
                <div className="text-xs text-slate-500">List cleaning</div>
              </div>
            </div>

            {/* Two-Column Layout: Upload (Left) + Pricing (Right) */}
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* LEFT CARD: Upload Section */}
              <BatchUploadSection
                onFileUploaded={handleFileUploaded}
                onError={handleFileUploadError}
              />

              {/* RIGHT CARD: Pricing Table */}
              <BatchPricingTable />
            </div>

          </div>
        </div>
      </section>

      {/* Data Analysis Results - Shows after upload */}
      {uploadedFile && (
        <section className="py-12 bg-slate-50" id="preview">
          <div className="max-w-6xl mx-auto px-4">
            <div className="ca-card ca-card-p">
              {/* Processing Type Selection */}
              <BatchProcessingTypeCard
                selectedType={selectedProcessingType}
                onTypeSelect={handleProcessingTypeSelect}
                processableRows={processableRows}
                aiOverviewEnabled={aiOverviewEnabled}
                onAiOverviewToggle={handleAiOverviewToggle}
              />

              {/* Action Button */}
              <BatchContinueButton
                onClick={handleContinueToProcessing}
                disabled={!uploadedFile}
                estimatedCost={estimatedCost}
                processableRows={processableRows}
                selectedProcessingType={selectedProcessingType}
              />
            </div>
          </div>
        </section>
      )}

      {/* Professional Features Section */}
      <section className="py-10 bg-white" id="features">
        <div className="max-w-7xl mx-auto px-4">
          <div className="mb-8 text-center">
            <h2 className="h1 mb-3">Enterprise-Grade Data Intelligence</h2>
            <p className="body max-w-3xl mx-auto text-slate-600">
              Process millions of records with the same data sources and algorithms used by major collection agencies and legal firms.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                <Shield className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="h2 mb-2">Identity Verification</h3>
              <p className="text-sm text-slate-600">
                Comprehensive identity validation using multiple data sources. Detect synthetic identities and flag high-risk profiles.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto mb-3 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                <Database className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="h2 mb-2">Data Enhancement</h3>
              <p className="text-sm text-slate-600">
                Complete missing contact information with current addresses, phone numbers, and email addresses from trusted sources.
              </p>
            </div>

            <div className="text-center">
              <div className="mx-auto mb-3 flex h-10 w-12 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                <BarChart3 className="h-5 w-5 text-slate-600" />
              </div>
              <h3 className="h2 mb-2">AI CollectABILITY Scoring</h3>
              <p className="text-sm text-slate-600">
                Advanced machine learning models predict recovery probability and optimal settlement amounts for each account.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center text-sm text-slate-500">
            &copy; 2024 CollectABILITY. Professional data processing platform.
          </div>
        </div>
      </footer>
    </div>
  );
};
